import { ChangeDetectorRef, Component, OnInit, ChangeDetectionStrategy, effect, inject } from '@angular/core';
import { NavbarComponent } from './components/shared/common/navbar/navbar.component';
import { RouterComponent } from './components/shared/common/main-router/router.component';
import { FooterComponent } from './components/shared/common/footer/footer.component';
import { LoaderEmmitterService } from './services/emmitters/loader.service';
import { SessionService } from './services/auth/session.service';
import { AuthorStoreService } from './services/stores/author-store.service';
import { UniverseStoreService } from './services/stores/universe-store.service';
import { combineLatest, finalize, timeout } from 'rxjs';
import { AppToastHostComponent } from './shared/toast/app-toast-host.component';
import { CatalogService } from './services/entities/catalog.service';
import { CollectionService } from './services/entities/collection.service';
import { getProductStateMessage } from './shared/api-error-message';
import { AppToastService } from './shared/toast/app-toast.service';
import { DecisionNoticeHostComponent } from './shared/notifications/decision-notice-host.component';
import { AdaptiveLayoutService } from './services/ui/adaptive-layout.service';
import { ConnectivityService } from './services/ui/connectivity.service';
import { PwaLifecycleService } from './services/ui/pwa-lifecycle.service';
import { MatIconModule } from '@angular/material/icon';
import { PresentationModeService } from './services/ui/presentation-mode.service';
import { NativeAppLinksService } from './services/native/native-app-links.service';
import { NativeRuntimeService } from './services/native/native-runtime.service';
import { AndroidReleaseUpdateService } from './services/native/android-release-update.service';
import { CommandPaletteService } from './services/ui/command-palette.service';
import { WebCommandPaletteComponent } from './components/web/ui/web-command-palette/web-command-palette.component';
import { NativeReaderIslandComponent } from './components/mobile/book/native-reader-island/native-reader-island.component';
import { NativeReaderSessionService } from './services/navigation/native-reader-session.service';
import { PushNotificationService } from './services/realtime/push-notification.service';
import { NativeKeyboardFocusService } from './services/native/native-keyboard-focus.service';
import { NativeNetworkFeedbackService } from './services/native/native-network-feedback.service';

@Component({
    standalone: true,
    selector:  'app-root',
    imports: [
        NavbarComponent,
        RouterComponent,
        FooterComponent,
        AppToastHostComponent,
        DecisionNoticeHostComponent,
        MatIconModule,
        NativeReaderIslandComponent,
        WebCommandPaletteComponent
    ],
    templateUrl: './app.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './app.component.sass'
})
export class AppComponent implements OnInit {
    readonly networkFeedback = inject(NativeNetworkFeedbackService);
    readonly commandPalette = inject(CommandPaletteService);
    title = 'Memoria bibliográfica';
    building: boolean = true;
    dragonLoader = 'assets/media/img/dragon1-unscreen.gif';
    loaderMessage = 'Cargando...';
    sessionReady = false;
    private loginLoaderMessages = [
        'El dragón está comprobando tus credenciales...',
        'Abriendo el portal de la biblioteca...',
        'Desempolvando tu grimorio personal...',
        'Invocando tu estantería secreta...',
        'El guardián de la biblioteca revisa tu pase...',
        'Consultando los archivos del reino...',
        'Afiliando tu alma lectora al gremio...',
        'Encendiendo las velas de lectura...',
        'El dragón está oliendo si eres tú...',
        'Buscando tu marca entre los pergaminos...',
        'Desbloqueando la puerta de la torre...',
        'El bibliotecario arcano está verificando tu identidad...',
        'Preparando tu rincón junto al fuego...',
        'Quitando telarañas digitales de tu cuenta...',
        'Abriendo la cubierta de tu aventura...',
        'El dragón está buscando tus gafas de leer...',
        'Comprobando que no seas un mimético disfrazado...',
        'Cargando tu santuario de historias...',
        'El sello mágico está casi listo...',
        'Despertando a los personajes que dejaste dormidos...',
        'Revisando si tienes permiso para entrar al archivo prohibido...',
        'El dragón está firmando tu entrada con tinta encantada...',
        'Afinando las runas de acceso...',
        'La biblioteca te reconoce... más o menos.',
        'Casi dentro. El dragón escribe lento, pero con buena letra.'
    ];
    private bookLoaderMessages = [
        'Abriendo el libro por la página correcta...',
        'El dragón está pasando las páginas con cuidado...',
        'Despertando a los personajes...',
        'Sacudiendo el polvo de la portada...',
        'Preparando tinta, papel y un poco de magia...',
        'Buscando el punto exacto donde te quedaste...',
        'La historia está afilando sus giros de guion...',
        'Cargando capítulos, secretos y alguna que otra desgracia...',
        'El narrador está tomando aire...',
        'Los personajes se están colocando en escena...',
        'Abriendo una puerta a otro mundo...',
        'El dragón está calentando el atril...',
        'Ordenando capítulos en la estantería astral...',
        'Reuniendo héroes, villanos y secundarios sospechosos...',
        'El libro está recordando cómo empezaba...',
        'Cuidado: aventura en proceso de invocación.',
        'Preparando la lámpara, la manta y el caos narrativo...',
        'La portada se está abriendo sola...',
        'Cargando mundos, mapas y decisiones cuestionables...',
        'El dragón está marcando tu última página...',
        'Los pergaminos se están desenrollando...',
        'Silencio, la historia está despertando...',
        'Invocando escenas pendientes...',
        'Los márgenes están susurrando spoilers. Ignóralos.',
        'El capítulo está entrando en escena con dramatismo.',
        'Ajustando el marcapáginas dimensional...',
        'Reanudando la expedición literaria...',
        'Poniendo a los protagonistas en posición de sufrir...',
        'El libro está abriendo sus fauces...',
        'Cargando una dosis aceptable de fantasía y café imaginario...'
    ];
    private extraLoaderMessages = [
        'El dragón olvidó la contraseña. Otra vez.',
        'Los personajes no quieren salir todavía.',
        'Buscando al protagonista. Se había ido a por pan.',
        'El villano está maquillándose. Un momento.',
        'El libro está cargando... y juzgando tus hábitos de sueño.',
        'El dragón insiste en releer el prólogo.',
        'Hay un mago bloqueando la página. Estamos negociando.',
        'Los capítulos se han desordenado. Culpa del duende.',
        'El narrador está improvisando con dignidad.',
        'El marcapáginas cayó en otra dimensión.',
        'Los secundarios piden más protagonismo. Ignóralos.',
        'El dragón está escribiendo "cargando" con caligrafía gótica.',
        'Preparando tragedias menores y aventuras mayores.',
        'La biblioteca está abierta, pero el dragón busca las llaves.',
        'Cargando... porque incluso la magia necesita un segundo.'
    ];
    private libraryRestored = false;
    private libraryRestorationInFlight = false;

    constructor(
        private loader: LoaderEmmitterService,
        private sessionSrv: SessionService,
        private collectionSrv: CollectionService,
        private universeStore: UniverseStoreService,
        private catalogSrv: CatalogService,
        private authorStore: AuthorStoreService,
        private cdRef: ChangeDetectorRef,
        private toasts: AppToastService,
        private adaptiveLayout: AdaptiveLayoutService,
        private presentationMode: PresentationModeService,
        private nativeAppLinks: NativeAppLinksService,
        private nativeRuntime: NativeRuntimeService,
        private nativeKeyboardFocus: NativeKeyboardFocusService,
        private androidUpdates: AndroidReleaseUpdateService,
        private nativeReader: NativeReaderSessionService,
        private pushNotifications: PushNotificationService,
        readonly connectivity: ConnectivityService,
        readonly pwa: PwaLifecycleService
    ) {
        effect(() => {
            const message = this.networkFeedback.message();
            if (message && this.sessionReady && !this.building) {
                this.toasts.showSystem(message, { title: 'Buscando otro camino', dedupeKey: 'native:ipv6-fallback', durationMs: 6000 });
            }
        });
    }


    ngOnInit(): void {
        this.nativeKeyboardFocus.initialize();
        void this.nativeReader.state();
        void this.pushNotifications.initializeNativeListeners().catch(() => this.toasts.showSystem(
            'Android no pudo preparar la apertura de notificaciones. Puedes reintentarlo al reiniciar la app.',
            { title: 'Notificaciones parcialmente disponibles', dedupeKey: 'native:push:listeners', durationMs: 8000 }
        ));
        void this.adaptiveLayout.snapshot;
        void this.presentationMode.snapshot;
        void this.nativeAppLinks.initialize();
        void this.nativeRuntime.initialize().catch(() => this.toasts.showSystem(
            'Android no pudo preparar todas las integraciones del dispositivo. Puedes continuar y volver a intentarlo al reiniciar la app.',
            { title: 'Integración nativa parcial', dedupeKey: 'native:runtime:init', durationMs: 8000 }
        ));
        void this.androidUpdates.check();
        this.loader.loaderStatus$.subscribe(value => {
            // Un mismo proceso puede emitir varias veces mientras sigue activo (por
            // ejemplo, el guard del libro confirma la carga iniciada desde la
            // tarjeta). La variante visual pertenece a la apertura del loader,
            // no a cada una de esas emisiones.
            if (value.active && !this.building) {
                this.loaderMessage = this.getRandomLoaderMessage(value.context);
            }
            this.building = value.active;
            this.cdRef.detectChanges();
        });

        this.sessionSrv.sessionInitializedSubject.subscribe(ready => {
            this.sessionReady = ready;
            this.cdRef.detectChanges();
        });

        combineLatest([this.connectivity.online$, this.sessionSrv.userIsLogged$]).subscribe(([online, logged]) => {
            if (!logged) {
                this.libraryRestored = false;
                return;
            }
            if (online)
                this.restoreLibrary();
        });
    }

    installApp(): void {
        void this.pwa.install();
    }

    retryConnection(): void {
        this.connectivity.retry();
    }

    private restoreLibrary(): void {
        if (!this.sessionSrv.canAccessLibrary || this.libraryRestored || this.libraryRestorationInFlight)
            return;

        this.libraryRestorationInFlight = true;
        this.loader.activateLoader('library');

        // La biblioteca es la superficie necesaria para entrar. El catálogo de
        // autores es auxiliar y no debe mantener toda la app bloqueada.
        this.collectionSrv.getUniverses().pipe(
            timeout({ first: 16_000 }),
            finalize(() => {
                this.libraryRestorationInFlight = false;
                this.loader.deactivateLoader();
            })
        ).subscribe({
            next: universes => {
                this.universeStore.setUniverses(universes);
                this.libraryRestored = true;
                this.catalogSrv.getAuthors().pipe(timeout({ first: 30000 })).subscribe({
                    next: authors => this.authorStore.setAuthors(authors),
                    error: () => this.toasts.showSystem('El catálogo de autores tardó demasiado. Se volverá a cargar cuando lo necesites.', {
                        title: 'Catálogo pendiente', dedupeKey: 'library:authors:timeout', durationMs: 6000
                    })
                });
            },
            error: (error) => {
                console.error('Error cargando datos iniciales.');
                // La autenticación ya terminó correctamente. Un fallo de datos
                // no invalida la sesión; los errores terminales de auth los
                // procesa ErrorInterceptorService antes de llegar aquí.
                if (!this.sessionSrv.userIsLogged)
                    return;
                const cause = getProductStateMessage(error, 'La API no ha permitido cargar tu biblioteca.');
                this.toasts.showSystem(`${cause} Tu sesión se conserva.`, {
                    title: 'No se pudo cargar la biblioteca',
                    dedupeKey: 'library:restore:error',
                    durationMs: 10000,
                    action: { label: 'Reintentar', execute: () => this.restoreLibrary() }
                });
            }
        });
    }

    private getRandomLoaderMessage(context: 'default' | 'login' | 'book' | 'library'): string {
        if (context === 'library')
            return 'Recuperando tu biblioteca…';
        if (context === 'login')
            return this.getRandomFrom(this.loginLoaderMessages);
        if (context === 'book')
            return this.getRandomFrom([...this.bookLoaderMessages, ...this.extraLoaderMessages]);
        return this.getRandomFrom(this.extraLoaderMessages);
    }

    private getRandomFrom(messages: string[]): string {
        const randomIndex = Math.floor(Math.random() * messages.length);
        return messages[randomIndex];
    }
}
